import {
  AuthorisedUser,
  ClearingHouseType,
  CollectionType,
  DefaultHeading,
  Order,
  OrderActions,
  OrderCreateArgs,
  ProductSector,
  SponsorType,
  SpreadsheetMappingType,
  TradingEntity
} from 'api'
import React from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { ModalConsumerProps, withModal } from '../modal'

import { Locale, Translation } from 'locale'
import { isNil, noop } from 'lodash'
import { enabledCollectionTypes } from 'model'
import { clearingHouseIdByTypeGetterSelector, sponsorIdByTypeGetterSelector } from 'selectors/clearingSelectors'
import { createAvailableCollectionIdsByNameAndTypeGetterSelector } from 'selectors/collectionSelectors'
import { createAuthorisedUserSelector } from 'selectors/connectionSelectors'
import { createOrdersWithCollectionIdGetterSelector } from 'selectors/orderSelectors'
import { allActiveTradingEntitiesSelector } from 'selectors/tradingEntitySelectors'
import '../upload/model'
import { parseClipboard } from '../upload/model'
import { SpreadsheetParseCompleteHandler, SpreadsheetParseDataHandler, SpreadsheetParseDialog } from '../upload/SpreadsheetParseDialog'
import { OrderMappingDialog } from './OrderMappingDialog'
import { OrderSpreadsheetParseContainer, ReplaceExistingBondsCheckbox } from './OrderUploadDialogStyles'
import { OrderSpreadsheetParseResult, parseOrderSpreadsheet } from './parseOrderSpreadsheet'
import { MAX_CREATE_ORDER_SPREADSHEET_CONTENT_LENGTH } from './utils'
import { productSectorByIsinBuiGetterSelector } from 'selectors'

export interface OrderUploadDialogProps extends ModalConsumerProps {
  initialFile?: File
  initialClipboard?: string
  authorisedUser: AuthorisedUser
  getAvailableCollectionIdsByName: (name: string, type: CollectionType | null) => string[]
  getOrdersInCollection: (collectionId: string) => Order[]
  getClearingHouseIdByType: (clearingHouse: ClearingHouseType) => string | null
  getSponsorIdByType: (sponsor: SponsorType) => string | null
  getProductSectorById: (collateralId: string) => ProductSector | null
  onClose?: () => unknown
  onCreateOrders: (args: OrderCreateArgs) => unknown
  allActiveTradingEntities: TradingEntity[]
}

interface OrderUploadDialogState {
  deleteCurrentOrdersInStaging: boolean
}

const mapStateToProps = createStructuredSelector({
  authorisedUser: createAuthorisedUserSelector(),
  getAvailableCollectionIdsByName: createAvailableCollectionIdsByNameAndTypeGetterSelector,
  getOrdersInCollection: createOrdersWithCollectionIdGetterSelector,
  getClearingHouseIdByType: clearingHouseIdByTypeGetterSelector,
  getSponsorIdByType: sponsorIdByTypeGetterSelector,
  getProductSectorById: productSectorByIsinBuiGetterSelector,
  allActiveTradingEntities: allActiveTradingEntitiesSelector
})

const mapDispatchToProps = {
  onCreateOrders: OrderActions.createWithResponse
}

export class OrderUploadDialogComponent extends React.Component<OrderUploadDialogProps, OrderUploadDialogState> {
  constructor(props: OrderUploadDialogProps) {
    super(props)
    this.state = {
      deleteCurrentOrdersInStaging: false
    }
  }

  onParseData: SpreadsheetParseDataHandler<OrderSpreadsheetParseResult> = async (rowData, filename, spreadsheetMapping) => {
    const {
      authorisedUser,
      getAvailableCollectionIdsByName,
      getOrdersInCollection,
      getClearingHouseIdByType,
      getSponsorIdByType,
      getProductSectorById,
      allActiveTradingEntities
    } = this.props

    if (isNil(rowData) || isNil(spreadsheetMapping)) {
      return Promise.reject()
    }

    return parseOrderSpreadsheet(
      filename,
      rowData,
      spreadsheetMapping,
      authorisedUser,
      getAvailableCollectionIdsByName,
      getOrdersInCollection,
      enabledCollectionTypes,
      getClearingHouseIdByType,
      getSponsorIdByType,
      getProductSectorById,
      allActiveTradingEntities
    )
  }

  onComplete: SpreadsheetParseCompleteHandler<OrderSpreadsheetParseResult> = ({ parseResult, rowData }) => {
    const { deleteCurrentOrdersInStaging } = this.state
    const { onCreateOrders, onClose = noop } = this.props

    onCreateOrders({
      deleteCurrentOrdersInStaging,
      orderAttributes: parseResult ? parseResult.parsedItems.reverse() : [],
      spreadsheetContent: JSON.stringify(rowData).slice(0, MAX_CREATE_ORDER_SPREADSHEET_CONTENT_LENGTH),
      ignoreAxeId: null,
      performUserValidations: false
    })

    onClose()
  }

  parseClipboard<Type>(clipboard: string) {
    return parseClipboard<Type>(clipboard, {
      dynamicTyping: (field: string) => {
        if (field === DefaultHeading.IsinBui) {
          return false
        }
        return true
      }
    })
  }

  render = () => (
    <SpreadsheetParseDialog<OrderSpreadsheetParseResult>
      clipboard={this.props.initialClipboard}
      parseClipboard={this.parseClipboard}
      container={OrderSpreadsheetParseContainer}
      getMappingModal={(title, mapping, parseResult) => (
        <OrderMappingDialog
          mapping={mapping}
          title={title}
          parseResult={parseResult}
          authorisedUser={this.props.authorisedUser}
          getAvailableCollectionIdsByName={this.props.getAvailableCollectionIdsByName}
          getOrdersInCollection={this.props.getOrdersInCollection}
          getClearingHouseIdByType={this.props.getClearingHouseIdByType}
          getSponsorIdByType={this.props.getSponsorIdByType}
          getProductSectorById={this.props.getProductSectorById}
          allActiveTradingEntities={this.props.allActiveTradingEntities}
        />
      )}
      onParseData={this.onParseData}
      onComplete={this.onComplete}
      onClose={this.props.onClose}
      parseResultItemsGetter={parseResult => parseResult.parsedItems}
      spreadsheetMappingType={SpreadsheetMappingType.STAGING}
      title={Locale.translate(Translation.TC_ADD_ASSETS)}
      translations={{
        resultSectionTitle: Translation.TC_COLUMN_MAPPINGS,
        emptyDropzoneNarrative: Translation.SC_UPLOAD_DROPZONE_NARRATIVE,
        parseSuccessMessage: Translation.SC_ORDER_SPREADSHEET_SUCCESS,
        parseErrorMessage: Translation.SC_ORDER_SPREADSHEET_ERROR,
        parseErrorNarrative: Translation.SC_UPLOAD_ERROR_NARRATIVE
      }}
      uploadFile={this.props.initialFile}>
      <ReplaceExistingBondsCheckbox
        label={Locale.translate(Translation.SC_REPLACE_EXISTING_BONDS)}
        isChecked={this.state.deleteCurrentOrdersInStaging}
        onUpdate={(deleteCurrentOrdersInStaging: boolean) => this.setState({ deleteCurrentOrdersInStaging })}
      />
    </SpreadsheetParseDialog>
  )
}

export const OrderUploadDialog = withModal(connect(mapStateToProps, mapDispatchToProps)(OrderUploadDialogComponent))
